import React, { ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { SxProps, Theme } from '@mui/material';

export interface ModalProps {
  visible: boolean;
  toggle?: () => void;
  modalContent: ReactNode | ((closeModal: () => void) => ReactNode);
  toggleable?: boolean;
  customClass?: string;
  headerClass?: string;
  headerText?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  dialogContentSx?: SxProps<Theme>;
}

export const Modal = ({
  visible,
  toggle = () => {},
  modalContent,
  toggleable = true,
  customClass,
  headerClass,
  headerText,
  maxWidth = 'md',
  fullWidth = true,
  sx,
  dialogContentSx,
}: ModalProps) => {
  const handleClose = () => {
    toggle();
  };

  const hasCustomWidth = Boolean(
    customClass &&
      (customClass.includes('max-w-') ||
        customClass.includes('w-') ||
        customClass.includes('modal')),
  );

  return (
    <Dialog
      open={Boolean(visible)}
      onClose={(event, reason) => {
        if (!toggleable && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        handleClose();
      }}
      maxWidth={hasCustomWidth ? false : maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        className: customClass,
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          m: 'auto',
          width: '100%',
          ...sx,
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(15, 23, 42, 0.45)',
        },
      }}
    >
      {/* Header */}
      {headerText && (
        <DialogTitle
          className={headerClass}
          sx={{
            m: 0,
            px: { xs: 2.5, sm: 3 },
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.125rem' },
              color: 'text.primary',
              letterSpacing: '-0.01em',
              pr: 2,
            }}
          >
            {headerText}
          </Typography>

          {toggleable && (
            <IconButton
              aria-label="close-modal"
              onClick={handleClose}
              size="small"
              sx={{
                color: 'text.secondary',
                bgcolor: 'grey.100',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'error.50',
                  color: 'error.main',
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </DialogTitle>
      )}

      {/* When no headerText but still toggleable, show floating close button */}
      {!headerText && toggleable && (
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
          <IconButton
            aria-label="close-modal"
            onClick={handleClose}
            size="small"
            sx={{
              color: 'text.secondary',
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              '&:hover': {
                bgcolor: 'error.50',
                color: 'error.main',
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Content */}
      <DialogContent
        sx={{
          p: { xs: 2, sm: 2.5 },
          bgcolor: 'background.paper',
          ...dialogContentSx,
        }}
      >
        {typeof modalContent === 'function'
          ? modalContent(handleClose)
          : modalContent}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
